import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Collapse, CollapseProvider, CollapseTrigger, CollapseToggle, Button, Grid, GridColumn, Icon, IIconProps, Text } from 'telia-front-react';
import { Disturbance, DisturbanceMapDisturbance, DisturbanceType } from '@config/DisturbanceConfig';
import TranslateStore from '@stores/TranslateStore';
import LanguageStore from '@stores/LanguageStore';
import DisturbanceModalStore from '@stores/DisturbanceModalStore';
import { getServiceInfo } from '@util/TextUtil';
import { formatDate } from '@util/DateUtil';

interface DisturbanceMapFailureProps {
  /**
   * Modal failure properties
   */
  refKey: string;
  isDealer: boolean;
  disturbance: DisturbanceMapDisturbance;
  languageStore?: LanguageStore;
  disturbanceModalStore?: DisturbanceModalStore;
  translateStore?: TranslateStore;
}

interface DisturbanceMapFailureLabelProps {
  /**
   * Modal failure label properties
   */
  label: string;
  children: React.ReactChild | React.ReactChild[];
}

const DisturbanceMapFailure = (props: DisturbanceMapFailureProps): JSX.Element => {
  const {
    refKey,
    isDealer,
    disturbance,
    languageStore: { currentLocale, currentLanguage },
    disturbanceModalStore,
    translateStore: { translate }
  } = props;
  const iconProps: IIconProps = {
    modifiers: disturbance.type === DisturbanceType.IncidentDisturbanceInfo ? '#alert' : '#calendar',
    size: 'sm'
  };

  const formatDisturbanceDate = (date: string, currentLocale: Locale) => {
    return `${translate('map.disturbance.time')} ${formatDate(date, 'HH:mm, dd. MMMM yyyy', currentLocale)}`;
  };

  const start: string = formatDisturbanceDate(disturbance.start, currentLocale);
  const plannedEnd: string = disturbance.plannedEnd ? formatDisturbanceDate(disturbance.plannedEnd, currentLocale) : null;
  const end: string = disturbance.actualEnd ? formatDisturbanceDate(disturbance.actualEnd, currentLocale) : null;
  const name: string = translate(disturbance.type === DisturbanceType.ChangeDisturbanceInfo ? 'map.disturbance.change' : 'map.disturbance.incident');
  const affected: number = disturbance.impactedCustomersCount;
  const disturbanceInfo: Disturbance = {} as Disturbance;
  const serviceInfo: string = getServiceInfo(disturbance.serviceInfo, currentLanguage);
  disturbanceInfo.id = disturbance.informationalId;

  return (
    <Grid noWrap={['sm']}>
      <GridColumn width={['xs-12', 'min-sm']} noGutter={true}>
        <Icon {...iconProps} />
      </GridColumn>
      <GridColumn width={['xs-12', 'max-sm']}>
        <Grid noWrap={['sm']}>
          <GridColumn width={['xs-12', 'sm-8']}>
            <DisturbanceMapFailureLabel label={`${translate('map.disturbance.interruption')}:`}>
              {name}{' '}
              {isDealer ? (
                <span>
                  {'('}ID:{' '}
                  <Button
                    id={refKey}
                    text={disturbance.informationalId}
                    link={true}
                    type="employee"
                    onClick={(event) => {
                      event.preventDefault();
                      disturbanceModalStore.openModal(disturbanceInfo, disturbance.type, refKey);
                    }}
                  />
                  {')'}
                </span>
              ) : (
                <></>
              )}
            </DisturbanceMapFailureLabel>
            <DisturbanceMapFailureLabel label={`${translate('map.disturbance.start')}:`}>{start}</DisturbanceMapFailureLabel>
            {plannedEnd || end ? (
              end ? (
                <DisturbanceMapFailureLabel label={`${translate('map.disturbance.end')}:`}>{end}</DisturbanceMapFailureLabel>
              ) : (
                <DisturbanceMapFailureLabel label={`${translate('map.disturbance.planned.end')}:`}>{plannedEnd}</DisturbanceMapFailureLabel>
              )
            ) : (
              <></>
            )}
            {affected && <DisturbanceMapFailureLabel label={`${translate('map.disturbance.affected')}:`}>{affected.toString()}</DisturbanceMapFailureLabel>}
          </GridColumn>
          {serviceInfo && (
            <GridColumn width={['xs-12', 'sm-4']}>
              <Text>
                <CollapseProvider id={`description-${refKey}`}>
                  <CollapseTrigger>
                    <CollapseToggle text={translate('map.disturbance.service.info')} link={true} type={'text-color'} iconRight="#arrow-down" />
                  </CollapseTrigger>
                  <Collapse noContainer={true}>{serviceInfo}</Collapse>
                </CollapseProvider>
              </Text>
            </GridColumn>
          )}
        </Grid>
      </GridColumn>
    </Grid>
  );
};

const DisturbanceMapFailureLabel = (props: DisturbanceMapFailureLabelProps): JSX.Element => {
  const { label, children } = props;
  return (
    <p>
      <Grid align={['middle-xs']} verticalGutter="none" element="span">
        <GridColumn width={['xs', 'sm-5', 'md-4']}>{label}</GridColumn>
        <GridColumn width={['xs']} className="text-bold">
          {children}
        </GridColumn>
      </Grid>
    </p>
  );
};

export default inject('disturbanceModalStore', 'languageStore', 'translateStore', 'disturbance', 'isDealer', 'refKey')(observer(DisturbanceMapFailure));
